import { useMutation, UseMutationResult, UseMutationOptions, MutationKey } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";
import { apiFetch } from "./apiFetch";

export type FetchMutationParams<TMutationResult, TPayload, TError = Error> = {
  mutationKey: MutationKey;
  url: string;
  method?: RequestInit["method"];
  options?: Omit<UseMutationOptions<TMutationResult, TError, TPayload>, "mutationKey" | "mutationFn">;
  requestHeaders?: HeadersInit;
  /**
   * Extra `fetch` options (e.g. `credentials`, `mode`, `cache`, `signal`) spread
   * onto the underlying request. `method`, `body`, and `headers` are managed by
   * this util and therefore excluded.
   */
  requestInit?: Omit<RequestInit, "method" | "body" | "headers">;
  getAccessToken?: () => Promise<string>;
};

async function executeMutationRequest<TMutationResult>({
  url,
  method,
  requestHeaders,
  requestInit,
  getAccessToken,
  body,
}: {
  url: string;
  method: RequestInit["method"];
  requestHeaders?: HeadersInit;
  requestInit?: Omit<RequestInit, "method" | "body" | "headers">;
  getAccessToken?: () => Promise<string>;
  body?: unknown;
}): Promise<TMutationResult> {
  let authToken: string | undefined;
  if (getAccessToken) authToken = await getAccessToken();
  const response = await apiFetch(url, {
    method,
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "X-REQUEST-ID": uuidv4(),
      "Content-Type": "application/json",
      ...requestHeaders,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...requestInit,
  });
  if (!response.ok) {
    const errorResponse = (await response.json()) as { message: string } | undefined;
    throw new Error(errorResponse?.message || response.statusText);
  }
  if (response.status === 204) return response.text() as Promise<TMutationResult>;
  return response.json() as Promise<TMutationResult>;
}

// returns mutation options
export function getFetchMutation<TMutationResult, TPayload, TError = Error>({
  mutationKey,
  url,
  method = "POST",
  options = {},
  requestHeaders = {},
  requestInit,
  getAccessToken,
}: FetchMutationParams<TMutationResult, TPayload, TError>): UseMutationOptions<TMutationResult, TError, TPayload> {
  const mutationFn = async (body?: TPayload) =>
    executeMutationRequest<TMutationResult>({ url, method, requestHeaders, requestInit, getAccessToken, body });
  return { mutationKey, mutationFn, ...options };
}

// returns hook to use in component
export function useFetchMutation<TMutationResult, TPayload = void, TError = Error>(
  params: Omit<FetchMutationParams<TMutationResult, TPayload, TError>, "getAccessToken">,
): UseMutationResult<TMutationResult, TError, TPayload> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const mutationOptions = getFetchMutation<TMutationResult, TPayload, TError>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useMutation<TMutationResult, TError, TPayload>(mutationOptions);
}

export type DynamicVariables<TPayload> = { url: string; body?: TPayload };

export type DynamicFetchMutationParams<TMutationResult, TPayload, TError = Error> = Omit<
  FetchMutationParams<TMutationResult, TPayload, TError>,
  "url" | "options"
> & {
  options?: Omit<UseMutationOptions<TMutationResult, TError, DynamicVariables<TPayload>>, "mutationKey" | "mutationFn">;
};

export function useDynamicFetchMutation<TMutationResult, TPayload = void, TError = Error>({
  mutationKey,
  method = "POST",
  options = {},
  requestHeaders = {},
  requestInit,
}: Omit<DynamicFetchMutationParams<TMutationResult, TPayload, TError>, "getAccessToken">): UseMutationResult<
  TMutationResult,
  TError,
  DynamicVariables<TPayload>
> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();

  return useMutation<TMutationResult, TError, DynamicVariables<TPayload>>({
    mutationKey,
    mutationFn: ({ url, body }) =>
      executeMutationRequest<TMutationResult>({
        url,
        method,
        requestHeaders,
        requestInit,
        getAccessToken: getAccessTokenSilently,
        body,
      }),
    ...options,
  });
}
